import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Maximize2, LifeBuoy, PictureInPicture2 } from "lucide-react";
import { PHASES } from "./PhaseTracker";
import { suggestedNextAction, firstMissingDiscoveryItem } from "../lib/guidance";
import MarkMomentButton from "./MarkMomentButton";

/**
 * Compact Closer Mode — minimal in-call view.
 *
 * Shows only:
 *   - Current phase pill
 *   - Suggested next action headline
 *   - Missing discovery items (only if in Discovery phase)
 *   - MARK MOMENT button
 *   - I Need Help button
 *   - "Expand to Full View" affordance
 *
 * Background extraction continues unchanged — this is purely a UI mask so
 * the closer isn't distracted by transcripts and categories during the call.
 */
const DISCOVERY = [
  { key: "pain_points", label: "Pain" },
  { key: "goals", label: "Goals" },
  { key: "previous_attempts", label: "Attempts" },
  { key: "cost_of_inaction", label: "Cost of Inaction" },
  { key: "decision_maker", label: "Decision Maker" },
  { key: "financial_situation", label: "Financial" },
];

export default function CompactCloserView({
  phase,
  state,
  markMoment,
  onOpenHelp,
  onExpand,
  onPopOut,
  isLive,
}) {
  const phaseMeta = PHASES.find((p) => p.id === phase) || PHASES[0];
  const nextAction = suggestedNextAction({ phase, state });
  const showDiscovery = phase === "discovery";

  return (
    <motion.section
      data-testid="compact-closer-view"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="max-w-2xl w-full mx-auto mt-12"
    >
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-display font-semibold mb-2">
          Current Phase
        </div>
        <h2
          data-testid="compact-phase-label"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
        >
          {phaseMeta.full}
        </h2>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-display font-semibold mb-2">
          Suggested next action
        </div>
        <p
          data-testid="compact-next-action"
          className="font-display text-xl sm:text-2xl leading-snug text-slate-900 mb-8"
        >
          {nextAction}
        </p>

        {showDiscovery && (
          <div className="mb-8" data-testid="compact-discovery">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-display font-semibold mb-3">
              Missing discovery
            </div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
              {DISCOVERY.map((it) => {
                const done = (state[it.key] || []).length > 0;
                return (
                  <li
                    key={it.key}
                    data-testid={`compact-discovery-${it.key}`}
                    className="flex items-center gap-2 text-sm"
                  >
                    {done ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-orange-500" />
                    )}
                    <span className={done ? "text-slate-500 line-through" : "text-slate-800 font-medium"}>
                      {it.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <MarkMomentButton onMark={markMoment} disabled={!isLive} />

        <div className="flex items-center justify-between mt-4 gap-3">
          <button
            type="button"
            onClick={onOpenHelp}
            data-testid="compact-help-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <LifeBuoy className="h-4 w-4" /> I Need Help
          </button>
          <button
            type="button"
            onClick={onExpand}
            data-testid="compact-expand-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 hover:border-slate-500 hover:bg-slate-50 text-slate-700 px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <Maximize2 className="h-4 w-4" /> Expand to Full View
          </button>
        </div>

        {onPopOut && (
          <button
            type="button"
            onClick={onPopOut}
            data-testid="compact-pop-out-btn"
            className="w-full mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 text-sm font-medium transition-colors"
            title="Open SCH as a floating window that stays above Meet/Zoom"
          >
            <PictureInPicture2 className="h-4 w-4" /> Pop Out Live Assistant
          </button>
        )}
      </div>

      <p className="text-xs text-slate-400 text-center mt-4">
        SCH is still extracting in the background. Switch to Full View any time to see categories and transcript.
      </p>
    </motion.section>
  );
}
